import { PointerEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export type ImageCrop = { x: number; y: number; width: number; height: number };
export const FULL_IMAGE_CROP: ImageCrop = { x: 0, y: 0, width: 1, height: 1 };

export async function cropCapturedImage(file: File, crop: ImageCrop): Promise<File> {
  if (!file.size || ![crop.x, crop.y, crop.width, crop.height].every(Number.isFinite) || crop.x < 0 || crop.y < 0
    || crop.width <= 0 || crop.height <= 0 || crop.x + crop.width > 1 + Number.EPSILON || crop.y + crop.height > 1 + Number.EPSILON) {
    throw new Error('선택한 이미지 영역을 확인해주세요.');
  }
  if (crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1) return file;

  const source = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => image.naturalWidth && image.naturalHeight ? resolve() : reject(new Error('캡처 사진을 읽을 수 없습니다. 다시 캡처해주세요.'));
      image.onerror = () => reject(new Error('캡처 사진을 읽을 수 없습니다. 다시 캡처해주세요.'));
      image.src = source;
    });
    let x = Math.min(image.naturalWidth - 1, Math.floor(crop.x * image.naturalWidth));
    let y = Math.min(image.naturalHeight - 1, Math.floor(crop.y * image.naturalHeight));
    let width = Math.min(image.naturalWidth, Math.ceil((crop.x + crop.width) * image.naturalWidth)) - x;
    let height = Math.min(image.naturalHeight, Math.ceil((crop.y + crop.height) * image.naturalHeight)) - y;
    // Round a square once so fractional source coordinates cannot create a one-pixel rectangle.
    if (Math.abs(crop.width * image.naturalWidth - crop.height * image.naturalHeight) < 0.000001) {
      width = height = Math.max(1, Math.round(crop.width * image.naturalWidth));
      x = Math.min(x, image.naturalWidth - width);
      y = Math.min(y, image.naturalHeight - height);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('이 브라우저에서는 이미지 영역 선택을 지원하지 않습니다.');
    context.drawImage(image, x, y, width, height, 0, 0, width, height);
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => result?.size ? resolve(result) : reject(new Error('선택 영역을 이미지로 만들지 못했습니다. 다시 시도해주세요.')), type, 0.92);
    });
    if (blob.size > 10 * 1024 * 1024) throw new Error('선택한 사진이 10MB를 초과했습니다. 더 작은 영역을 선택해주세요.');
    return new File([blob], `live-capture-crop-${Date.now()}.${type === 'image/png' ? 'png' : 'jpg'}`, { type });
  } finally {
    image.onload = null;
    image.onerror = null;
    URL.revokeObjectURL(source);
  }
}

type Props = {
  src: string;
  disabled?: boolean;
  onCropChange: (crop: ImageCrop) => void;
  imageBounds?: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;
  fitContainer?: boolean;
  onReadyChange?: (ready: boolean) => void;
};

export default function CaptureImageEditor({ src, disabled = false, onCropChange, imageBounds, fitContainer = false, onReadyChange }: Props) {
  const [crop, setCrop] = useState<ImageCrop>(FULL_IMAGE_CROP);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [displaySize, setDisplaySize] = useState<{ width: number; height: number }>();
  const drag = useRef<{ x: number; y: number; pointerId: number; moving: boolean; crop: ImageCrop } | null>(null);
  const onCropChangeRef = useRef(onCropChange);
  onCropChangeRef.current = onCropChange;
  const instructionsId = useId();

  useLayoutEffect(() => {
    setCrop(FULL_IMAGE_CROP);
    setLoaded(false);
    setDisplaySize(undefined);
    setLoadError(false);
    drag.current = null;
    onCropChangeRef.current(FULL_IMAGE_CROP);
    return () => { drag.current = null; };
  }, [src]);
  useEffect(() => { if (disabled) drag.current = null; }, [disabled]);
  useEffect(() => { onReadyChange?.(loaded); }, [loaded, onReadyChange]);

  const centeredCrop = () => {
    const image = imageRef.current;
    const rect = image?.getBoundingClientRect();
    const width = image?.naturalWidth || rect?.width;
    const height = image?.naturalHeight || rect?.height;
    if (!width || !height) return FULL_IMAGE_CROP;
    const side = Math.min(width, height) * .8;
    return { x: (1 - side / width) / 2, y: (1 - side / height) / 2, width: side / width, height: side / height };
  };

  useLayoutEffect(() => {
    const image = imageRef.current;
    const stage = stageRef.current;
    if (!loaded || !image || !stage || !image.naturalWidth || !image.naturalHeight) return;
    const view = stage.ownerDocument.defaultView ?? window;
    const fit = () => {
      const bounds = stage.getBoundingClientRect();
      const availableHeight = imageBounds || fitContainer ? bounds.height : view.innerHeight * .4;
      if (!bounds.width || !availableHeight) return;
      const scale = Math.min(1, bounds.width / image.naturalWidth, availableHeight / image.naturalHeight);
      const next = { width: image.naturalWidth * scale, height: image.naturalHeight * scale };
      setDisplaySize(previous => previous?.width === next.width && previous.height === next.height ? previous : next);
    };
    fit();
    let frame = 0;
    const scheduleFit = () => {
      view.cancelAnimationFrame(frame);
      frame = view.requestAnimationFrame(fit);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleFit);
    observer?.observe(stage);
    view.addEventListener('resize', fit);
    return () => { observer?.disconnect(); view.cancelAnimationFrame(frame); view.removeEventListener('resize', fit); };
  }, [loaded, src, fitContainer, imageBounds]);

  const update = (next: ImageCrop) => {
    if (disabled || !loaded) return;
    setCrop(next);
    onCropChangeRef.current(next);
  };
  const point = (event: PointerEvent<HTMLDivElement>) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return null;
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      rect,
    };
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || !drag.current || drag.current.pointerId !== event.pointerId) return;
    const current = point(event);
    if (!current) return;
    if (drag.current.moving) {
      const original = drag.current.crop;
      update({ ...original,
        x: Math.max(0, Math.min(1 - original.width, original.x + current.x - drag.current.x)),
        y: Math.max(0, Math.min(1 - original.height, original.y + current.y - drag.current.y)),
      });
      return;
    }
    const sourceWidth = imageRef.current?.naturalWidth || current.rect.width;
    const sourceHeight = imageRef.current?.naturalHeight || current.rect.height;
    const dx = current.x - drag.current.x;
    const dy = current.y - drag.current.y;
    const side = Math.min(
      Math.max(Math.abs(dx) * sourceWidth, Math.abs(dy) * sourceHeight),
      (dx < 0 ? drag.current.x : 1 - drag.current.x) * sourceWidth,
      (dy < 0 ? drag.current.y : 1 - drag.current.y) * sourceHeight,
    );
    const width = side / sourceWidth;
    const height = side / sourceHeight;
    if (width * current.rect.width < 2 || height * current.rect.height < 2) return;
    update({ x: drag.current.x - (dx < 0 ? width : 0), y: drag.current.y - (dy < 0 ? height : 0), width, height });
  };
  const changePercent = (key: keyof ImageCrop, value: string) => {
    if (!value.trim() || !Number.isFinite(Number(value))) return;
    const amount = Math.max(key === 'x' || key === 'y' ? 0 : 0.01, Math.min(1, Number(value) / 100));
    if (key === 'x') update({ ...crop, x: Math.min(amount, 1 - crop.width) });
    if (key === 'y') update({ ...crop, y: Math.min(amount, 1 - crop.height) });
    if (key === 'width' || key === 'height') {
      const image = imageRef.current;
      const rect = image?.getBoundingClientRect();
      const width = image?.naturalWidth || rect?.width;
      const height = image?.naturalHeight || rect?.height;
      if (!width || !height) return;
      const side = Math.min(amount * (key === 'width' ? width : height), (1 - crop.x) * width, (1 - crop.y) * height);
      update({ ...crop, width: side / width, height: side / height });
    }
  };

  return <Editor $fitContainer={fitContainer}>
    <Stage ref={stageRef} $fitContainer={fitContainer} $overlay={!!imageBounds} style={imageBounds ? { position: 'fixed', ...imageBounds } : undefined}>
      <ImageArea
        style={displaySize}
        aria-describedby={instructionsId}
        $disabled={disabled || !loaded}
        onPointerDown={event => {
          if (disabled || !loaded || event.button !== 0 || event.isPrimary === false) return;
          const start = point(event);
          if (!start) return;
          event.preventDefault();
          const target = event.target as HTMLElement;
          const resizing = Boolean(target.closest('[data-crop-resize]'));
          drag.current = { x: resizing ? crop.x : start.x, y: resizing ? crop.y : start.y,
            pointerId: event.pointerId, moving: !resizing && Boolean(target.closest('[data-crop-selection]')), crop };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={move}
        onPointerUp={event => {
          if (drag.current?.pointerId !== event.pointerId) return;
          move(event);
          drag.current = null;
          if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={event => { if (drag.current?.pointerId === event.pointerId) drag.current = null; }}
        onLostPointerCapture={event => { if (drag.current?.pointerId === event.pointerId) drag.current = null; }}
      >
        <img ref={imageRef} src={src} alt="상품에 등록할 방송 캡처" draggable={false} onLoad={() => {
          const initial = centeredCrop();
          setCrop(initial);
          onCropChangeRef.current(initial);
          setLoaded(true);
        }} onError={() => { setLoaded(false); setLoadError(true); }} />
        {loaded && <Selection data-crop-selection role="group" aria-label="캡처 영역" tabIndex={disabled ? -1 : 0}
          onKeyDown={event => {
            if (event.target !== event.currentTarget) return;
            const delta = event.shiftKey ? .05 : .01;
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            update({ ...crop,
              x: Math.max(0, Math.min(1 - crop.width, crop.x + (event.key === 'ArrowLeft' ? -delta : event.key === 'ArrowRight' ? delta : 0))),
              y: Math.max(0, Math.min(1 - crop.height, crop.y + (event.key === 'ArrowUp' ? -delta : event.key === 'ArrowDown' ? delta : 0))),
            });
          }}
          style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }}>
          <ResizeHandle type="button" data-crop-resize aria-label="캡처 영역 크기 조절" disabled={disabled}
            onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -2 : 2;
              changePercent('width', String(crop.width * 100 + delta));
            }}><span /></ResizeHandle>
        </Selection>}
      </ImageArea>
    </Stage>
    {loadError && <p role="alert">캡처 사진을 표시할 수 없습니다. 다시 캡처해주세요.</p>}
    <Toolbar>
      <p id={instructionsId}>영역을 드래그해 이동하고 오른쪽 아래 모서리로 크기를 조절하세요.</p>
      <button type="button" disabled={disabled || !loaded} onClick={() => update(centeredCrop())}>중앙으로 초기화</button>
    </Toolbar>
    <details>
      <summary>영역 직접 조정</summary>
      <Controls disabled={disabled || !loaded}>
        <legend>선택 영역 비율</legend>
        {([
          ['x', '왼쪽 위치'], ['y', '위쪽 위치'], ['width', '영역 너비'], ['height', '영역 높이'],
        ] as const).map(([key, label]) => <label key={key}>{label} (%)<input type="number" min={key === 'x' || key === 'y' ? 0 : 1} max={100} step={1} value={Math.round(crop[key] * 10000) / 100} onChange={event => changePercent(key, event.target.value)} /></label>)}
      </Controls>
    </details>
  </Editor>;
}

const Editor = styled.div<{ $fitContainer: boolean }>`
  display: flex; flex-direction: column; gap: 12px; min-width: 0;
  ${({ $fitContainer }) => $fitContainer && 'width: 100%; height: 100%; min-height: 0; gap: 0;'}
  p { margin: 0; font-size: 14px; line-height: 1.5; color: var(--admin-theme-text, #5b5269); }
  p[role='alert'] { color: var(--admin-theme-danger, #a32121); }
  button, input { min-height: 44px; border: 1px solid var(--admin-theme-border, #d9d4e2); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #332b40); font: inherit; }
  button { padding: 8px 12px; cursor: pointer; white-space: nowrap; }
  button:hover:not(:disabled) { background: var(--admin-theme-accent-soft, #f7f4fc); border-color: #8464dd; }
  button:disabled, fieldset:disabled { opacity: 0.55; }
  button:disabled { cursor: not-allowed; }
  button:focus-visible, input:focus-visible, summary:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  summary { cursor: pointer; min-height: 44px; display: flex; align-items: center; font-size: 14px; color: var(--admin-theme-link, #5730b4); text-decoration: underline; text-underline-offset: 3px; }
`;
const Stage = styled.div<{ $overlay: boolean; $fitContainer: boolean }>`
  display: flex; align-items: center; justify-content: center; background: var(--admin-theme-raised, #f5f3f8); border-radius: 10px; overflow: hidden;
  ${({ $fitContainer }) => $fitContainer && 'flex: 1; min-height: 0; width: 100%; border-radius: 0; background: #18161c;'}
  ${({ $overlay }) => $overlay && 'border-radius: 0;'}
`;
const ImageArea = styled.div<{ $disabled: boolean }>`
  position: relative; flex-shrink: 0; width: fit-content; max-width: 100%; overflow: hidden; line-height: 0;
  touch-action: none; user-select: none; cursor: ${props => props.$disabled ? 'default' : 'crosshair'};
  img { display: block; width: 100%; height: 100%; object-fit: contain; }
`;
const Selection = styled.div`
  position: absolute; box-sizing: border-box; cursor: move; border: 2px solid #fff; outline: 1px solid #6841c6;
  box-shadow: 0 0 0 100vmax rgba(20, 13, 32, 0.55);
  &:focus-visible { outline: 3px solid #a993ef; }
`;
const ResizeHandle = styled.button`
  && { position: absolute; right: -2px; bottom: -2px; width: 44px; height: 44px; min-height: 44px; padding: 0; border: 0; border-radius: 0; background: transparent; cursor: nwse-resize; }
  &&:hover:not(:disabled) { background: rgba(255,255,255,.12); }
  span { position: absolute; right: 0; bottom: 0; width: 12px; height: 12px; border-right: 4px solid #fff; border-bottom: 4px solid #fff; }
`;
const Toolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`;
const Controls = styled.fieldset`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 12px 0 0; margin: 0; border: 0;
  legend { padding: 0; font-size: 14px; color: var(--admin-theme-text, #5b5269); }
  label { display: flex; flex-direction: column; gap: 6px; font-size: 14px; }
  input { min-width: 0; width: 100%; box-sizing: border-box; padding: 8px 10px; font-size: 16px; }
`;
